import React, {useEffect, useId} from 'react'
/** @jsx jsx */
import {jsx, css} from '@emotion/react'
import {useLocation} from 'react-router-dom'
import PropTypes from 'prop-types'
import {Box} from '@chakra-ui/react'
import {useProducts} from '@salesforce/commerce-sdk-react'
import usePDCResponsiveElement from '@app/hooks/use-pdc-responsive-element.js'
import {ProductCarousel} from '@app/components/product-carousel/index.jsx'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'
import {HOME_HREF} from '../../../constants'
import useShipToCountry from '../../../hooks/use-ship-to-country'

/**
 * PDCAboutProductSlider is a React functional component that renders a responsive product slider section,
 * typically used to showcase a curated list of products in a carousel format on a page. The component dynamically
 * determines the number of slides to show based on the current screen size and configuration props, and fetches
 * product data based on tile definitions provided via the `regions` prop. It integrates with Google Tag Manager (GTM)
 * to trigger view events for analytics, and adapts its behavior based on the user's location and shipping country.
 *
 * @param {Object} props - The component props.
 * @param {number} props.smallNbSlidesToShow - Number of slides to show on small screens.
 * @param {number} props.mediumNbSlidesToShow - Number of slides to show on medium screens.
 * @param {number} props.largeNbSlidesToShow - Number of slides to show on large screens.
 * @param {number} props.xlargeNbSlidesToShow - Number of slides to show on extra-large screens.
 * @param {number} props.xxlargeNbSlidesToShow - Number of slides to show on extra-extra-large screens.
 * @param {boolean} props.isSlider - Whether to render the carousel as a slider.
 * @param {string} props.imageType - The type of product image to display (main and hover).
 * @param {number} props.displayPriority - Determines the display priority of the slider.
 * @param {Array} props.regions - Array of region objects containing tile definitions for the slider.
 *
 * @returns {JSX.Element} A section element containing a responsive product carousel, styled and configured according to the provided props.
 *
 * @example
 * <PDCAboutProductSlider
 *   smallNbSlidesToShow={1}
 *   mediumNbSlidesToShow={2}
 *   largeNbSlidesToShow={3}
 *   xlargeNbSlidesToShow={4}
 *   xxlargeNbSlidesToShow={5}
 *   isSlider={true}
 *   imageType="main"
 *   displayPriority={1}
 *   regions={regionsData}
 * />
 */
export const PDCAboutProductSlider = ({
    smallNbSlidesToShow,
    mediumNbSlidesToShow,
    largeNbSlidesToShow,
    xlargeNbSlidesToShow,
    xxlargeNbSlidesToShow,
    isSlider,
    imageType,
    displayPriority,
    regions
}) => {
    const {triggerViewItemListEvent} = useGTM()
    const location = useLocation()
    const isHomePage = location.pathname.includes(HOME_HREF)
    const {shipToCountry} = useShipToCountry()

    // Get the tiles components from the regions
    let tiles = []
    if (regions) {
        regions.forEach((region) => {
            if (region.id === 'pdc_about_product_slider_tiles') {
                tiles = region.components
            }
        })
    }

    // Get products
    const productIds = tiles.map((tile) => tile?.data?.product)
    const {data: products, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: productIds?.join(','),
                allImages: true,
                expand: ['promotions', 'prices', 'images', 'variations'],
                c_isHomePage: isHomePage,
                c_country: shipToCountry?.id
            }
        },
        {enabled: Boolean(productIds?.length > 0 && shipToCountry?.id)}
    )

    useEffect(() => {
        if (!isProductsLoading) {
            triggerViewItemListEvent(products?.data, 'homepage')
        }
    }, [isProductsLoading])

    // Get the number of slides to show depending on the screen size
    const nbSlidesToShowByBreakpoint = usePDCResponsiveElement({
        smallEl: smallNbSlidesToShow || 1,
        mediumEl: mediumNbSlidesToShow || 2,
        largeEl: largeNbSlidesToShow,
        xlargeEl: xlargeNbSlidesToShow,
        xxlargeEl: xxlargeNbSlidesToShow
    })

    const titleId = useId()

    return (
        <Box as="section" aria-describedby={titleId} css={[PDCAboutProductSlider.css]}>
            <ProductCarousel
                nbSlidesToShow={nbSlidesToShowByBreakpoint}
                showProductImages={true}
                showProductNames={true}
                showProductPrices={true}
                showProductColorVariations={true}
                products={products?.data}
                isLoading={isProductsLoading}
                mainImageType={imageType}
                hoverImageType={imageType}
                isSlider={isSlider}
                py={['56px', null, '70px']}
                isFavIconInTitle={true}
                className={'pdc-about-product-slider'}
                isArrowsNextToTitle={false}
                displayPriority={displayPriority}
                smallTileRatio={2 / 3}
                tileTitleHeading="p"
            />
        </Box>
    )
}

PDCAboutProductSlider.css = css`
    .pdc-about-product-slider {
        .slick-slider {
            padding-right: calc(100vw / 4);

            @media screen and (min-width: 768px) {
                padding-right: 0;
                margin-left: 0;
                margin-right: 0;
            }
        }
        .slick-list {
            @media screen and (min-width: 768px) {
                overflow: visible;
            }
        }
        .grid-list {
            display: flex;
        }
        .slick-slide,
        .grid-tile {
            flex: 1;
            margin-left: 0;
            padding-left: 10px;
            padding-right: 33px;

            @media screen and (min-width: 768px) {
                margin-right: 0;
                padding-left: 20px;
                padding-right: 40px;
            }
        }
    }
`

PDCAboutProductSlider.propTypes = {
    title: PropTypes.string.isRequired,
    smallNbSlidesToShow: PropTypes.number,
    mediumNbSlidesToShow: PropTypes.number,
    largeNbSlidesToShow: PropTypes.number,
    xlargeNbSlidesToShow: PropTypes.number,
    xxlargeNbSlidesToShow: PropTypes.number,
    isSlider: PropTypes.bool,
    imageType: PropTypes.string,
    displayPriority: PropTypes.bool,
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object,
                    id: PropTypes.string,
                    typeId: PropTypes.string
                })
            )
        })
    )
}
